/**
 * Copyright (c) 2021-2026 Kognitos, Inc. All rights reserved.
 */
import Intercom from '@intercom/messenger-js-sdk';
import { useConfig } from '@/config/ConfigProvider';
import { useCurrentUser } from '@/generated/hooks/agora/v1/user';

interface IIntercomProviderProps {
  children: React.ReactNode;
}

/**
 * Initializes the Intercom Messenger with user identity when available.
 *
 * Reads `intercomAppId` from runtime config and user data from `useCurrentUser`.
 * When `intercomAppId` is absent or empty, renders children without Intercom.
 * The SDK tracks instances internally, so re-renders will not reboot the messenger.
 *
 * @param {IIntercomProviderProps} props - Component props
 * @param {React.ReactNode} props.children - Child components to render
 * @returns {JSX.Element} Children, with Intercom initialized as a side-effect
 */
export function IntercomProvider({ children }: IIntercomProviderProps) {
  const config = useConfig();
  const { data: currentUser } = useCurrentUser();

  const userId = currentUser?.data?.user?.name;
  const email = currentUser?.data?.user?.email;

  // Only initialize Intercom when a valid app ID is configured
  if (config.intercomAppId) {
    Intercom({
      app_id: config.intercomAppId,
      // Hide Intercom's floating bottom-right launcher and open it on the
      // left, triggered by the sidebar Help button (MAN-3562). The button
      // positions and toggles the messenger imperatively (see `HelpButton`).
      hide_default_launcher: true,
      alignment: 'left',
      ...(userId && { user_id: userId }),
      ...(email && { email }),
    });
  }

  return <>{children}</>;
}
